import { Container, Button } from "@mui/material";
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useUserContext, useWebSocketContext } from "../contexts";
import PageInfo from "./PageInfo/PageInfo";
import loadingComponent from "./LoadingComponent";

function WaitingRoom() {
  const location = useLocation();
  const state = location.state || {};
  const { sport_id } = state;
  const socket = useWebSocketContext();
  const { username } = useUserContext();
  const navigate = useNavigate();

  useEffect(() => {
    window.history.forward();
    if (!sport_id) navigate("/play");

    const handleBeforeUnload = () => {
      if (!socket) return;
      socket.send(
        JSON.stringify({ type: "leaveWaitingRoom", sport_id: sport_id, userId: username })
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleBeforeUnload);

    return () => {
      if (!socket) return;
      socket.send(
        JSON.stringify({ type: "leaveWaitingRoom", sport_id: sport_id, userId: username })
      );
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleBeforeUnload);
    };
  }, [socket, sport_id, username, navigate]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        navigate("/play");
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [navigate]);

  useEffect(() => {
    if (!socket) return;
    if (socket.readyState === WebSocket.OPEN) {
      // Send findMatch message (per integration guide)
      socket.send(
        JSON.stringify({
          type: "findMatch",
          sport_id: parseInt(sport_id),
        })
      );
    }

    const handleGameStart = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === "matchFound") {
        // Match found! Auto-join the match (per integration guide)
        console.log('Match found!', data);
        socket.send(
          JSON.stringify({
            type: "joinMatch",
            gameId: data.gameId
          })
        );
        // Navigation will happen on rejoinGame message
      } else if (data.type === "rejoinGame") {
        // Game joined successfully, navigate to game page
        console.log('Rejoining game:', data);
        navigate(`/play/game/?gameId=${data.gameId}&sportId=${sport_id}`);
      }
    };

    socket.addEventListener("message", handleGameStart);
    return () => {
      socket.removeEventListener("message", handleGameStart);
    };
  }, [socket?.readyState, navigate, sport_id, socket]);

  const handleCancelSearch = () => {
    console.log('Cancel search clicked', { socket: !!socket, readyState: socket?.readyState, sport_id, username });

    if (!socket) {
      console.warn('No socket available, navigating anyway');
      navigate("/play");
      return;
    }

    if (socket.readyState === WebSocket.OPEN) {
      console.log('Sending leaveWaitingRoom message');
      socket.send(
        JSON.stringify({ type: "leaveWaitingRoom", sport_id: sport_id, userId: username })
      );
    } else {
      console.warn('Socket not open, state:', socket.readyState);
    }

    navigate("/play");
  };

  return (
    <>
      <PageInfo
        title="Finding Match - Crossover Grid"
        metaTitle="Finding Match - Crossover Grid Multiplayer"
        metaDescription="Finding a multiplayer opponent for your Crossover Grid match. Get ready to test your sports knowledge in real-time!"
        metaUrl={location.pathname}
      />
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          width: "100vw",
        }}
      >
        {loadingComponent("Searching for a match...", null)}
        <Button
          variant="contained"
          color="secondary"
          onClick={handleCancelSearch}
          sx={{ mt: 2 }}
        >
          Cancel Search
        </Button>
      </Container>
    </>
  );
}

export default WaitingRoom;
